import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import {
    Plus, ChevronDown, ChevronRight, Search, X, Trash2, Save, Upload, Download,
    AlertTriangle, CheckCircle, XCircle, RotateCcw, MoreVertical,
} from 'lucide-react';
import { LibraryFormData } from '../../types/LibraryTemplate';
import { downloadCsv, csvDateSuffix, escapeCsvField, formatCsvBaseTypes } from '../../Helpers/csvUtils';
import { BaseTypeMultiSelect } from '../../Components/BaseTypeMultiSelect';
import { LibraryConfig, libraryGrouping, usesBaseTypes } from '../../library/LibraryConfig';
import { LibraryImportIssue, LibraryImportRow, parseLibraryCsv } from '../../library/libraryCsvImport';
import { LibraryBatchItem } from '../../api/libraryApi';
import { useLibrary, emptyLibraryForm } from '../../hooks/library/useLibrary';
import SuperadminBreadcrumb from '../Superadmin/SuperadminBreadcrumb';
import './LibraryPage.css';

type ImportIssue = LibraryImportIssue;

/** 'merge' keeps templates that are not in the file; 'replace' makes the library exactly the file. */
type ImportMode = 'merge' | 'replace';

interface ImportResult {
    createdNames: string[];
    updatedNames: string[];
    skipped: number;
    issues: ImportIssue[];
    /** True when the file was refused as a whole and nothing was written */
    rejected: boolean;
    /** Set when the whole library was replaced ('replace' mode) */
    replaced?: { before: number; after: number; removedNames: string[] };
}

/** A parsed 'replace' import waiting for the user's confirmation. Nothing is written yet. */
interface ReplacePreview<F extends object> {
    rows: LibraryImportRow<F>[];
    issues: ImportIssue[];
    skipped: number;
    /** Number of templates in the library when the file was parsed */
    currentCount: number;
    /** Names of the existing templates that are not in the file */
    removedNames: string[];
}

interface LibraryPageProps<F extends object> {
    config: LibraryConfig<F>;
}

/**
 * Superadmin page for one template library.
 * Split-pane layout: left side shows templates grouped (by base type unless
 * the config has its own grouping),
 * right side shows the editor for the selected template.
 * Includes CSV import/export and a reset-library action.
 *
 * Everything library-specific comes from `config`; access control is
 * handled by the superadmin route guard in App.js.
 */
export function LibraryPage<F extends object>({ config }: LibraryPageProps<F>) {
    const { labels, csv } = config;
    const Icon = labels.icon;
    const withBaseTypes = usesBaseTypes(config);
    const grouping = useMemo(() => libraryGrouping(config), [config]);

    const {
        templates,
        loading,
        error,
        selectedTemplateId,
        selectedTemplate,
        selectTemplate,
        editForm,
        setEditForm,
        saving,
        saveError,
        getFilteredTemplates,
        getTemplatesByGroup,
        loadTemplates,
        saveTemplate,
        deleteTemplate: deleteTemplateFromHook,
        addTemplate,
        resetLibrary: resetLibraryFromHook,
        batchUpsertTemplates,
        replaceAllTemplates,
    } = useLibrary(config);

    // UI-only state
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

    // Add new template modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState<LibraryFormData<F>>(() => emptyLibraryForm(config));
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    // Delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Reset-entire-library confirmation modal
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);

    // CSV Import modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importMode, setImportMode] = useState<ImportMode>('merge');
    const [importing, setImporting] = useState(false);
    const [isImportDragOver, setIsImportDragOver] = useState(false);
    const [replacePreview, setReplacePreview] = useState<ReplacePreview<F> | null>(null);
    const [replaceError, setReplaceError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [expandedIssueSections, setExpandedIssueSections] = useState<Set<string>>(new Set());

    // Group import issues by label, separated by severity
    const shownIssues = importResult?.issues ?? replacePreview?.issues;
    const { errorGroups, warningGroups, errorCount, warningCount } = useMemo(() => {
        const errorGroups: Record<string, ImportIssue[]> = {};
        const warningGroups: Record<string, ImportIssue[]> = {};
        let errorCount = 0;
        let warningCount = 0;

        for (const issue of shownIssues ?? []) {
            const groups = issue.severity === 'error' ? errorGroups : warningGroups;
            (groups[issue.label] ??= []).push(issue);
            if (issue.severity === 'error') {
                errorCount++;
            } else {
                warningCount++;
            }
        }

        return { errorGroups, warningGroups, errorCount, warningCount };
    }, [shownIssues]);

    const toggleIssueSection = useCallback((label: string) => {
        setExpandedIssueSections((prev) => {
            const next = new Set(prev);
            if (next.has(label)) {
                next.delete(label);
            } else {
                next.add(label);
            }
            return next;
        });
    }, []);

    // Derived data from hook
    const filteredTemplates = useMemo(() => getFilteredTemplates(searchTerm), [getFilteredTemplates, searchTerm]);
    const templatesByGroup = useMemo(() => getTemplatesByGroup(searchTerm), [getTemplatesByGroup, searchTerm]);

    const toggleSection = useCallback((groupKey: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    }, []);

    const handleSave = async () => {
        await saveTemplate();
    };

    const handleDelete = async () => {
        if (!selectedTemplateId) return;

        setDeleting(true);
        try {
            const success = await deleteTemplateFromHook();
            if (success) {
                setShowDeleteModal(false);
            }
        } finally {
            setDeleting(false);
        }
    };

    const closeResetModal = () => {
        if (resetting) return;
        setShowResetModal(false);
        setResetError(null);
    };

    // Deletes the entire library via the hook. Irreversible.
    const handleReset = async () => {
        setResetting(true);
        setResetError(null);
        try {
            await resetLibraryFromHook();
            setShowResetModal(false);
            selectTemplate(null);
        } catch (err) {
            console.error('Failed to reset library:', err);
            setResetError(err instanceof Error ? err.message : 'Kunde inte återställa biblioteket');
        } finally {
            setResetting(false);
        }
    };

    const handleAdd = async () => {
        setAdding(true);
        setAddError(null);

        try {
            const newId = await addTemplate(addForm);
            if (newId) {
                setShowAddModal(false);
                setAddForm(emptyLibraryForm(config));
                selectTemplate(newId);
            }
        } catch (err) {
            console.error('Failed to add library template:', err);
            setAddError(err instanceof Error ? err.message : 'Kunde inte skapa mallen');
        } finally {
            setAdding(false);
        }
    };

    // ============================================================
    // CSV import
    // ============================================================

    const csvHeader = ['Namn', ...csv.columns, ...(withBaseTypes ? ['Bastyper'] : [])].join(';');

    /**
     * Parse the whole file, then write everything in one batch. A file with
     * any error is rejected as a whole: nothing is written until every row
     * conforms. Warnings (e.g. ignored base types) are ok.
     *
     * 'merge' writes straight away. 'replace' stops at a preview of what
     * disappears; the write happens in handleConfirmReplace.
     */
    const handleImport = async () => {
        if (!importFile) return;

        setImporting(true);
        setImportResult(null);

        try {
            const { rows, issues, skipped, hasErrors } = parseLibraryCsv(await importFile.text(), csv, withBaseTypes);

            if (hasErrors) {
                setImportResult({ createdNames: [], updatedNames: [], skipped, issues, rejected: true });
                return;
            }

            if (importMode === 'replace') {
                if (rows.length === 0 && templates.length === 0) {
                    setImportResult({ createdNames: [], updatedNames: [], skipped, issues, rejected: false });
                    return;
                }

                const importedIds = new Set(rows.map((row) => row.id));
                setReplaceError(null);
                setReplacePreview({
                    rows,
                    issues,
                    skipped,
                    currentCount: templates.length,
                    removedNames: templates.filter((t) => !importedIds.has(t.id)).map((t) => t.translations.sv),
                });
                return;
            }

            const existingIds = new Set(templates.map((t) => t.id));
            const toWrite: Array<LibraryBatchItem<F> & { name: string }> = rows.map((row) => ({
                ...row,
                isUpdate: existingIds.has(row.id),
            }));

            if (toWrite.length > 0) {
                await batchUpsertTemplates(toWrite.map(({ id, data, isUpdate }) => ({ id, data, isUpdate })));
            }

            setImportResult({
                createdNames: toWrite.filter((t) => !t.isUpdate).map((t) => t.name),
                updatedNames: toWrite.filter((t) => t.isUpdate).map((t) => t.name),
                skipped,
                issues,
                rejected: false,
            });

            await loadTemplates();
        } catch (err) {
            console.error('Import failed:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte läsa filen';
            setImportResult({
                createdNames: [],
                updatedNames: [],
                skipped: 0,
                issues: [{ row: 0, name: '', label: message, detail: message, severity: 'error' }],
                rejected: true,
            });
        } finally {
            setImporting(false);
        }
    };

    // Writes the previewed replace: the library becomes exactly the file's templates. Irreversible.
    const handleConfirmReplace = async () => {
        if (!replacePreview) return;

        setImporting(true);
        setReplaceError(null);

        try {
            await replaceAllTemplates(replacePreview.rows.map(({ id, data }) => ({ id, data })));

            setImportResult({
                createdNames: [],
                updatedNames: [],
                skipped: replacePreview.skipped,
                issues: replacePreview.issues,
                rejected: false,
                replaced: {
                    before: replacePreview.currentCount,
                    after: replacePreview.rows.length,
                    removedNames: replacePreview.removedNames,
                },
            });
            setReplacePreview(null);
            selectTemplate(null);

            await loadTemplates();
        } catch (err) {
            console.error('Failed to replace library:', err);
            setReplaceError(err instanceof Error ? err.message : 'Kunde inte skriva över biblioteket');
        } finally {
            setImporting(false);
        }
    };

    const resetImportModal = () => {
        if (importing) return;
        setShowImportModal(false);
        setImportFile(null);
        setImportMode('merge');
        setImportResult(null);
        setReplacePreview(null);
        setReplaceError(null);
        setExpandedIssueSections(new Set());
        setIsImportDragOver(false);
    };

    // Drag and drop handlers for CSV import
    const handleImportDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsImportDragOver(true);
    };

    const handleImportDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsImportDragOver(false);
        }
    };

    const handleImportDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsImportDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const csvFile = files.find((f) => f.name.toLowerCase().endsWith('.csv'));
        if (csvFile) {
            setImportFile(csvFile);
        }
    };

    // Export templates to CSV (symmetric with handleImport via csvUtils)
    const handleExport = () => {
        const rows = templates.map((template) =>
            [
                template.translations.sv,
                ...csv.formatColumns(template),
                ...(withBaseTypes ? [formatCsvBaseTypes(template.baseTypes)] : []),
            ]
                .map(escapeCsvField)
                .join(';')
        );
        downloadCsv(`${labels.exportFilePrefix}_${csvDateSuffix()}.csv`, csvHeader, rows);
    };

    // ============================================================
    // Render helpers
    // ============================================================

    const renderIssueGroups = (groups: Record<string, ImportIssue[]>, colorClass: string, badgeClass: string) =>
        Object.entries(groups).map(([label, groupIssues]) => (
            <div key={label} className="border-bottom last:border-0">
                <button
                    type="button"
                    className="btn btn-link text-decoration-none w-100 text-start d-flex align-items-center justify-content-between py-2 px-3"
                    onClick={() => toggleIssueSection(label)}
                >
                    <span className="d-flex align-items-center gap-2">
                        {expandedIssueSections.has(label) ? (
                            <ChevronDown size={16} className="text-muted" />
                        ) : (
                            <ChevronRight size={16} className="text-muted" />
                        )}
                        <span className={colorClass}>{label}</span>
                    </span>
                    <span className={`badge ${badgeClass}`}>{groupIssues.length}</span>
                </button>
                {expandedIssueSections.has(label) && (
                    <div className="px-3 pb-2">
                        {groupIssues.map((issue, idx) => (
                            <div key={idx} className="small py-1 ps-4 border-top">
                                <span className="text-muted">Rad {issue.row}:</span>{' '}
                                {issue.name && <strong>"{issue.name}"</strong>}
                                {issue.detail !== issue.label && (
                                    <span className="text-muted"> — {issue.detail}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ));

    const renderWarnings = () =>
        warningCount > 0 && (
            <div>
                <h6 className="text-warning mb-2">
                    <AlertTriangle size={16} className="me-1" />
                    Varningar ({warningCount})
                </h6>
                <div className="border border-warning rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {renderIssueGroups(warningGroups, 'text-warning', 'bg-warning text-dark')}
                </div>
            </div>
        );

    const renderRemovedNames = (names: string[]) =>
        names.length > 0 ? (
            <div className="mb-3">
                <h6 className="text-danger mb-2">
                    <Trash2 size={16} className="me-1" />
                    Tas bort ({names.length})
                </h6>
                {renderNameList(names, 'border-danger')}
            </div>
        ) : (
            <p className="text-muted">Inga mallar tas bort.</p>
        );

    // Confirmation step of a 'replace' import: what the library has, what it gets, what disappears.
    const renderReplacePreview = (preview: ReplacePreview<F>) => (
        <div>
            {preview.rows.length === 0 ? (
                <div className="alert alert-danger d-flex align-items-start mb-3">
                    <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                    <div>
                        <strong>Filen innehåller inga mallar.</strong> Om du fortsätter raderas hela{' '}
                        {labels.resetNoun}, alla {preview.currentCount} mallar. Vill du verkligen radera biblioteket?
                    </div>
                </div>
            ) : (
                <>
                    <p>
                        Biblioteket har <strong>{preview.currentCount} mallar</strong> och ersätts med{' '}
                        <strong>{preview.rows.length} mallar</strong> från filen.
                    </p>
                    {renderRemovedNames(preview.removedNames)}
                </>
            )}
            <p className="mb-2">
                Åtgärden går <strong>inte att ångra</strong>. Exportera en säkerhetskopia först om du vill kunna
                återställa biblioteket.
            </p>
            <Button
                variant="outline-secondary"
                size="sm"
                className="mb-3"
                onClick={handleExport}
                disabled={templates.length === 0}
            >
                <Download size={16} className="me-1" />
                Exportera säkerhetskopia (CSV)
            </Button>
            {renderWarnings()}
            {replaceError && <div className="alert alert-danger mt-3 mb-0">{replaceError}</div>}
        </div>
    );

    const renderNameList = (names: string[], borderClass: string) => (
        <div className={`border ${borderClass} rounded px-3 py-2`} style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {names.map((name, idx) => (
                <div key={idx} className="small py-1">
                    {name}
                </div>
            ))}
        </div>
    );

    const renderTemplateItem = (template: { id: string; translations: { sv: string } }, key: string) => (
        <div
            key={key}
            className={`template-item ${selectedTemplateId === template.id ? 'selected' : ''}`}
            onClick={() => selectTemplate(template.id)}
        >
            <span className="template-item-name">{template.translations.sv}</span>
        </div>
    );

    return (
        <div className="library-page-shell">
        <SuperadminBreadcrumb title={labels.title} />
        <div className="library-page-wrapper">
            <div className="library-header">
                <h1 className="page-title">
                    <Icon size={24} className="me-2" />
                    {labels.title}
                </h1>
                <p className="text-muted mb-0">{labels.description}</p>
            </div>

            {/* Modals live outside the loading branch so a refetch after
                add/import does not unmount them (and their results). */}

            {/* Add Template Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ny mall i biblioteket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Namn (svenska)</Form.Label>
                        <Form.Control
                            type="text"
                            value={addForm.name}
                            onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder={labels.addNamePlaceholder}
                            disabled={adding}
                            autoFocus
                        />
                    </Form.Group>

                    {withBaseTypes && (
                        <Form.Group className="mb-3">
                            <Form.Label>Bastyper</Form.Label>
                            <BaseTypeMultiSelect
                                selected={addForm.baseTypes}
                                onChange={(baseTypes) => setAddForm((prev) => ({ ...prev, baseTypes }))}
                                disabled={adding}
                            />
                        </Form.Group>
                    )}

                    {config.renderFields(addForm, setAddForm, adding)}

                    {addError && <div className="alert alert-danger mt-3 mb-0">{addError}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={adding}>
                        Avbryt
                    </Button>
                    <Button variant="primary" onClick={handleAdd} disabled={adding || !addForm.name.trim()}>
                        {adding ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Skapar...
                            </>
                        ) : (
                            'Skapa'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ta bort mall</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Är du säker på att du vill ta bort mallen{' '}
                        <strong>{selectedTemplate?.translations.sv}</strong>?
                    </p>
                    <p className="text-muted mb-0">{labels.deleteNote}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Tar bort...
                            </>
                        ) : (
                            'Ta bort'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reset Library Modal */}
            <Modal show={showResetModal} onHide={closeResetModal} centered backdrop="static">
                <Modal.Header closeButton={!resetting}>
                    <Modal.Title className="text-danger d-flex align-items-center">
                        <AlertTriangle size={20} className="me-2" />
                        Återställ hela biblioteket
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Detta tar bort <strong>alla {templates.length} mallar</strong> i {labels.resetNoun} permanent.
                    </p>
                    <div className="alert alert-danger d-flex align-items-start mb-3">
                        <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div>
                            Åtgärden går <strong>inte att ångra</strong>. Det finns ingen papperskorg och raderade
                            mallar kan inte återställas.
                        </div>
                    </div>
                    <p className="mb-2">
                        Vi rekommenderar <strong>starkt</strong> att du exporterar en säkerhetskopia först. Du kan
                        importera den igen om du ångrar dig.
                    </p>
                    <Button variant="outline-secondary" size="sm" onClick={handleExport} disabled={templates.length === 0}>
                        <Download size={16} className="me-1" />
                        Exportera säkerhetskopia (CSV)
                    </Button>
                    {resetError && <div className="alert alert-danger mt-3 mb-0">{resetError}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeResetModal} disabled={resetting}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleReset} disabled={resetting}>
                        {resetting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Återställer...
                            </>
                        ) : (
                            <>
                                <Trash2 size={16} className="me-1" />
                                Radera alla mallar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* CSV Import Modal */}
            <Modal show={showImportModal} onHide={resetImportModal} centered size="lg">
                <Modal.Header closeButton={!importing}>
                    <Modal.Title>
                        <Upload size={20} className="me-2" />
                        Importera mallar från CSV
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {replacePreview ? (
                        renderReplacePreview(replacePreview)
                    ) : !importResult ? (
                        <>
                            <div
                                onDragOver={handleImportDragOver}
                                onDragLeave={handleImportDragLeave}
                                onDrop={handleImportDrop}
                                style={{
                                    padding: '12px',
                                    marginBottom: '1rem',
                                    borderRadius: '8px',
                                    border: isImportDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                    backgroundColor: isImportDragOver ? '#f0f8ff' : 'transparent',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <Form.Label>Välj CSV-fil</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => {
                                        const file = (e.target as HTMLInputElement).files?.[0];
                                        setImportFile(file || null);
                                    }}
                                    disabled={importing}
                                />
                                <Form.Text className="text-muted d-block mt-1">
                                    {labels.csvHelp} Du kan också dra och släppa filen här.
                                </Form.Text>
                            </div>

                            <fieldset disabled={importing}>
                                <legend className="form-label fs-6">Befintligt bibliotek</legend>
                                <Form.Check
                                    type="radio"
                                    name="library-import-mode"
                                    id="library-import-mode-merge"
                                    className="mb-2"
                                    checked={importMode === 'merge'}
                                    onChange={() => setImportMode('merge')}
                                    label={
                                        <>
                                            Komplettera biblioteket
                                            <span className="d-block small text-muted">
                                                Mallar i filen skapas eller uppdateras. Övriga mallar behålls.
                                            </span>
                                        </>
                                    }
                                />
                                <Form.Check
                                    type="radio"
                                    name="library-import-mode"
                                    id="library-import-mode-replace"
                                    checked={importMode === 'replace'}
                                    onChange={() => setImportMode('replace')}
                                    label={
                                        <>
                                            Skriv över biblioteket
                                            <span className="d-block small text-muted">
                                                Biblioteket innehåller efteråt bara mallarna i filen. Du får se vilka
                                                mallar som tas bort innan något ändras.
                                            </span>
                                        </>
                                    }
                                />
                            </fieldset>
                        </>
                    ) : (
                        <div>
                            {importResult.rejected && (
                                <div className="alert alert-danger d-flex align-items-start mb-3">
                                    <XCircle size={18} className="me-2 mt-1 flex-shrink-0" />
                                    <div>
                                        <strong>Filen importerades inte.</strong> Inga mallar skapades eller
                                        uppdaterades. Rätta felen nedan och försök igen.
                                    </div>
                                </div>
                            )}

                            {importResult.replaced && (
                                <>
                                    <p className="text-success">
                                        <CheckCircle size={16} className="me-1" />
                                        Biblioteket skrevs över: det hade {importResult.replaced.before} mallar och
                                        har nu {importResult.replaced.after}.
                                    </p>
                                    {renderRemovedNames(importResult.replaced.removedNames)}
                                </>
                            )}

                            {importResult.createdNames.length > 0 && (
                                <div className="mb-3">
                                    <h6 className="text-success mb-2">
                                        <CheckCircle size={16} className="me-1" />
                                        Skapade ({importResult.createdNames.length})
                                    </h6>
                                    {renderNameList(importResult.createdNames, 'border-success')}
                                </div>
                            )}

                            {importResult.updatedNames.length > 0 && (
                                <div className="mb-3">
                                    <h6 className="text-primary mb-2">
                                        <CheckCircle size={16} className="me-1" />
                                        Uppdaterade ({importResult.updatedNames.length})
                                    </h6>
                                    {renderNameList(importResult.updatedNames, 'border-primary')}
                                </div>
                            )}

                            {!importResult.replaced &&
                                importResult.createdNames.length === 0 &&
                                importResult.updatedNames.length === 0 &&
                                importResult.skipped === 0 && (
                                    <div className="text-muted mb-3">Inga ändringar gjordes</div>
                                )}

                            {errorCount > 0 && (
                                <div className="mb-3">
                                    <h6 className="text-danger mb-2">
                                        <XCircle size={16} className="me-1" />
                                        {importResult.rejected ? 'Fel' : 'Överhoppade'} ({errorCount})
                                    </h6>
                                    <div className="border border-danger rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {renderIssueGroups(errorGroups, 'text-danger', 'bg-danger')}
                                    </div>
                                </div>
                            )}

                            {renderWarnings()}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {replacePreview ? (
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setReplacePreview(null);
                                    setReplaceError(null);
                                }}
                                disabled={importing}
                            >
                                Tillbaka
                            </Button>
                            <Button variant="danger" onClick={handleConfirmReplace} disabled={importing}>
                                {importing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Skriver över...
                                    </>
                                ) : replacePreview.rows.length === 0 ? (
                                    <>
                                        <Trash2 size={16} className="me-1" />
                                        Radera alla mallar
                                    </>
                                ) : (
                                    'Skriv över biblioteket'
                                )}
                            </Button>
                        </>
                    ) : !importResult ? (
                        <>
                            <Button variant="secondary" onClick={resetImportModal} disabled={importing}>
                                Avbryt
                            </Button>
                            <Button variant="primary" onClick={handleImport} disabled={importing || !importFile}>
                                {importing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Importerar...
                                    </>
                                ) : (
                                    'Starta import'
                                )}
                            </Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={resetImportModal}>
                            Stäng
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {!loading && !error && (
                <div className="library-page">
                    {/* Left Pane - Template List */}
                    <div className="library-left-pane">
                        <div className="left-pane-header">
                            <div className="left-pane-actions-row">
                                <div className="actions-left">
                                    <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                                        <Plus size={16} className="me-1" />
                                        Lägg till
                                    </Button>
                                    <Form.Select
                                        size="sm"
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value as 'grouped' | 'flat')}
                                        className="view-mode-select"
                                    >
                                        <option value="grouped">{grouping.viewLabel}</option>
                                        <option value="flat">Alfabetisk lista</option>
                                    </Form.Select>
                                </div>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        size="sm"
                                        className="library-actions-menu-toggle"
                                        title="Fler åtgärder"
                                    >
                                        <MoreVertical size={16} />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu align="end">
                                        <Dropdown.Item onClick={() => setShowImportModal(true)}>
                                            <Upload size={16} className="me-2" />
                                            Importera från CSV...
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={handleExport} disabled={templates.length === 0}>
                                            <Download size={16} className="me-2" />
                                            Exportera till CSV...
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            onClick={() => {
                                                setResetError(null);
                                                setShowResetModal(true);
                                            }}
                                            disabled={templates.length === 0}
                                            className="text-danger"
                                        >
                                            <RotateCcw size={16} className="me-2" />
                                            Återställ hela biblioteket...
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div className="left-pane-search-row">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">
                                        <Search size={14} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Sök mall..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="template-list-container">
                            {viewMode === 'grouped' &&
                                grouping.groups.map((group) => {
                                    const templatesForGroup = templatesByGroup[group.key] || [];
                                    const isCollapsed = collapsedSections.has(group.key);

                                    if (templatesForGroup.length === 0) return null;

                                    return (
                                        <div key={group.key} className="base-type-section">
                                            <div
                                                className="base-type-header"
                                                onClick={() => toggleSection(group.key)}
                                                role="button"
                                                aria-expanded={!isCollapsed}
                                            >
                                                {isCollapsed ? (
                                                    <ChevronRight size={16} className="base-type-chevron" />
                                                ) : (
                                                    <ChevronDown size={16} className="base-type-chevron" />
                                                )}
                                                <span className="base-type-header-name">{group.label}</span>
                                                <span className="badge bg-secondary">{templatesForGroup.length}</span>
                                            </div>

                                            {!isCollapsed && (
                                                <div className="template-list">
                                                    {templatesForGroup.map((template) =>
                                                        renderTemplateItem(template, `${group.key}-${template.id}`)
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                            {viewMode === 'flat' && (
                                <div className="template-list">
                                    {filteredTemplates.map((template) => renderTemplateItem(template, template.id))}
                                </div>
                            )}

                            {filteredTemplates.length === 0 && (
                                <div className="text-center text-muted py-4">
                                    {searchTerm ? (
                                        <p>Inga mallar matchar "{searchTerm}"</p>
                                    ) : (
                                        <p>Inga mallar i biblioteket</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Pane - Edit Form */}
                    <div className="library-right-pane">
                        {selectedTemplate ? (
                            <div className="template-editor">
                                <h5 className="mb-4">{selectedTemplate.translations.sv}</h5>

                                <Form.Group className="mb-3">
                                    <Form.Label>Namn (svenska)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                        disabled={saving}
                                    />
                                </Form.Group>

                                {withBaseTypes && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Bastyper</Form.Label>
                                        <BaseTypeMultiSelect
                                            selected={editForm.baseTypes}
                                            onChange={(baseTypes) => setEditForm((prev) => ({ ...prev, baseTypes }))}
                                            disabled={saving}
                                        />
                                    </Form.Group>
                                )}

                                <div className="mb-4">{config.renderFields(editForm, setEditForm, saving)}</div>

                                {saveError && <div className="alert alert-danger">{saveError}</div>}

                                <div className="d-flex gap-2">
                                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Sparar...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} className="me-1" />
                                                Spara
                                            </>
                                        )}
                                    </Button>
                                    <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)} disabled={saving}>
                                        <Trash2 size={16} className="me-1" />
                                        Ta bort
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="text-muted">Välj en mall från listan för att visa och redigera</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
        </div>
    );
}

export default LibraryPage;
